import {type TestIdProps} from 'shared/web';

import type {
  ActivePreset,
  GetDataQueryResponse,
  TransformedDefinitionApiResponse,
} from '../types/Api';
import type {EagColumn} from '../types/EagColumn';
import type {EagColumnGroup} from '../types/EagColumnGroup';
import {addColumnToGroupedArray} from './addColumnToGroupedArray';
import {columnToBasicDefs} from './columnToBasicDefs';
import {columnToFilterDefs} from './columnToFilterDefs';
import {columnToTypeDefs} from './columnToTypeDefs';

/** Runtime sort entry returned by the API but not yet added to the public preset types. */
type PresetSortEntry = {column: string; order: 'ASC' | 'DESC'};

/** Extends the canonical `ActivePreset` union to include the `sort` array that the
 *  API returns at runtime but that is not yet reflected in `Api.ts`. */
type ActivePresetWithSort = ActivePreset & {sort?: PresetSortEntry[]};

export type EagColumnOrGroup = EagColumn | EagColumnGroup;

/**
 * Builds the per-column lookup Maps derived from the active preset, keyed by column key:
 * - `colIndexMap` — preset column order index (drives the sort comparator)
 * - `colMap` — the preset's column settings entry
 * - `sortMap` — the preset's sort entry + its index
 *
 * O(n) construction — replaces O(n²) `.findIndex()`/`.find()` in the sort comparator and
 * forEach body of `transformDefinition`.
 */
function buildPresetLookups(activePreset?: ActivePresetWithSort): {
  colIndexMap: Map<string, number>;
  colMap: Map<string, ActivePresetWithSort['columnsSettings'][number]>;
  sortMap: Map<string, {entry: PresetSortEntry; index: number}>;
} {
  const colIndexMap = new Map<string, number>();
  const colMap = new Map<string, ActivePresetWithSort['columnsSettings'][number]>();
  if (activePreset?.columnsSettings) {
    activePreset.columnsSettings.forEach((col, i) => {
      colIndexMap.set(col.columnKey, i);
      colMap.set(col.columnKey, col);
    });
  }

  const sortMap = new Map<string, {entry: PresetSortEntry; index: number}>();
  if (activePreset?.sort) {
    activePreset.sort.forEach((sort, i) => {
      sortMap.set(sort.column, {entry: sort, index: i});
    });
  }

  return {colIndexMap, colMap, sortMap};
}

export type TransformDefinitionProps = TestIdProps & {
  definition: TransformedDefinitionApiResponse;
  columnOverrides?: Record<string, Partial<EagColumn>>;
  activePreset?: ActivePresetWithSort;
  dataQuery: GetDataQueryResponse;
};

export const transformDefinition = (props: TransformDefinitionProps): EagColumnOrGroup[] => {
  const definition = props.definition;

  if (!definition) {
    return [];
  }

  const columns = definition.columns;
  const groups = definition.groups;
  const activePreset = props.activePreset;
  const columnOverrides = props.columnOverrides;

  const {
    colIndexMap: presetColIndexMap,
    colMap: presetColMap,
    sortMap: presetSortMap,
  } = buildPresetLookups(activePreset);

  const colDefsArr: EagColumnOrGroup[] = [];

  columns
    .toSorted((a, b) => {
      if (!activePreset) {
        return 0;
      }
      const aIndex = presetColIndexMap.get(a.key) ?? -1;
      const bIndex = presetColIndexMap.get(b.key) ?? -1;
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return -1;
      }
      return aIndex - bIndex;
    })
    .forEach((column) => {
      const colOverride = (columnOverrides ?? {})[column.key];
      const defaultPresetCol = presetColMap.get(column.key);

      const sortData = presetSortMap.get(column.key);
      const defaultPresetSort = sortData?.entry;
      const defaultPresetSortIndex = sortData ? sortData.index : -1;

      const colDef: EagColumn = {
        ...columnToBasicDefs(
          props.dataQuery,
          definition,
          column,
          defaultPresetCol,
          defaultPresetSort,
          defaultPresetSortIndex === -1 ? undefined : defaultPresetSortIndex
        ),
        ...columnToTypeDefs(column),
        ...columnToFilterDefs(column),
        ...colOverride,
      } as EagColumn;

      addColumnToGroupedArray(colDefsArr, colDef, column.group, groups);
    });

  return colDefsArr;
};
